import React, { useReducer, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { Link, Flex, Button } from 'components/ui';
import Pill from 'components/Pill';
import FilterGroup from 'components/ProductPage/ShadeFilter/FilterGroup';
import skuUtils from 'utils/Sku';
import localeUtils from 'utils/LanguageLocale';
import skuHelpers from 'utils/skuHelpers';
import { useIsSmallView } from 'components/ProductPage/hooks/useIsSmallView';
import ShadeFilterEN from 'components/ProductPage/ShadeFilter/locales/ShadeFilter_en_US';
import ShadeFilterFR from 'components/ProductPage/ShadeFilter/locales/ShadeFilter_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(ShadeFilterEN, ShadeFilterFR);

const initialState = {
    selectedFilters: [],
    appliedFilters: [],
    isDropdownOpen: false,
    isModalOpened: false
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'UPDATE_SELECTED':
            return { ...state, selectedFilters: action.filters };
        case 'TOGGLE_DROPDOWN':
            return {
                ...state,
                isDropdownOpen: action.isOpen,
                selectedFilters: action.isOpen ? state.appliedFilters : state.selectedFilters
            };
        case 'TOGGLE_MODAL':
            return {
                ...state,
                isModalOpened: action.isOpen,
                selectedFilters: action.isOpen ? state.appliedFilters : state.selectedFilters
            };
        case 'APPLY_FILTERS':
            return {
                ...state,
                isModalOpened: false,
                selectedFilters: action.filters,
                appliedFilters: action.filters,
                isDropdownOpen: !state.isDropdownOpen
            };
        case 'RESET_FILTERS':
            return {
                ...state,
                isModalOpened: false,
                selectedFilters: [],
                appliedFilters: [],
                isDropdownOpen: !state.isDropdownOpen
            };
        default:
            return state;
    }
};

const ShadeFilter = ({ currentProduct, applyFilters }) => {
    useSelector(state => state.user.beautyInsiderAccount, shallowEqual);
    const isSmallView = useIsSmallView();
    const [state, dispatch] = useReducer(reducer, initialState);
    const { selectedFilters, appliedFilters, isDropdownOpen, isModalOpened } = state;
    const dropdownRef = useRef(null);

    const skus = (currentProduct.regularChildSkus || []).concat(currentProduct.onSaleChildSkus || []);

    const applyGalleryFiltersCallback = newAppliedFilters => {
        const newFilters = { skuId: newAppliedFilters.map(filter => filter.skuId) };
        applyFilters && applyFilters(newFilters);
    };

    const applyGalleryFilters = (e, isReset = false) => {
        if (!isModalOpened && dropdownRef.current) {
            dropdownRef.current.triggerDropdown(e, false);
        }

        const filters = isReset ? [] : selectedFilters;
        dispatch({ type: isReset ? 'RESET_FILTERS' : 'APPLY_FILTERS', filters });
        applyGalleryFiltersCallback(filters);
    };

    const buttons = (
        <Flex
            alignItems='center'
            justifyContent='space-between'
        >
            <Link
                data-at={Sephora.debug.dataAt('swatch_clear_btn')}
                color='blue'
                padding={2}
                margin={-2}
                onClick={e => applyGalleryFilters(e, true)}
            >
                {getText('clear')}
            </Link>
            <Button
                data-at={Sephora.debug.dataAt('swatch_done_btn')}
                variant='primary'
                hasMinWidth={true}
                onClick={e => applyGalleryFilters(e)}
            >
                {getText('done')}
            </Button>
        </Flex>
    );

    const hasColorFilter = currentProduct.variationType === skuUtils.skuVariationType.COLOR && skus.length > 0;
    const colorMatchSku = skuHelpers.getColorIQMatchSku(currentProduct.regularChildSkus);
    const showColorMatch = skuUtils.showColorIQOnPPage(currentProduct) ? colorMatchSku : false;
    const colorMatchSkuId = showColorMatch && showColorMatch.skuId;
    const isActive = isSmallView ? isModalOpened : isDropdownOpen;

    return hasColorFilter ? (
        <FilterGroup
            id='shade_filter'
            skus={skus}
            showModal={isOpen => dispatch({ type: 'TOGGLE_MODAL', isOpen })}
            isModalOpened={isModalOpened}
            ref={dropdownRef}
            colorMatchSkuId={colorMatchSkuId}
            selectedFilters={selectedFilters}
            buttons={buttons}
            updateFilters={(filters, shouldTriggerCallBack) => {
                dispatch({ type: 'UPDATE_SELECTED', filters });

                if (shouldTriggerCallBack) {
                    applyGalleryFiltersCallback(state.appliedFilters);
                }
            }}
            toggleDropdownOpen={(e, isOpen) => dispatch({ type: 'TOGGLE_DROPDOWN', isOpen })}
            isSmallView={isSmallView}
            itemType={currentProduct.swatchType}
        >
            <Pill
                isActive={isActive || appliedFilters.length > 0}
                fontSize='sm'
                hasArrow={true}
            >
                {getText('shade')}
                {appliedFilters.length > 0 && ` (${appliedFilters.length})`}
            </Pill>
        </FilterGroup>
    ) : null;
};

export default wrapFunctionalComponent(ShadeFilter, 'ShadeFilter');
